import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { VehicleCard } from "../../types/vtc";
import { getFavourites } from "../../lib/marketplaceApi";
import { setFavouriteListing } from "../../lib/driverApi";
import { useDriver } from "../../lib/driverContext";
import { errorText } from "../../lib/api";
import { toast } from "../../lib/toast";
import { PageTitle } from "../../components/company/ui";
import VehicleCardItem from "../../components/home/VehicleCardItem";
import { useT } from "../../i18n";

/** Saved vehicles (GET /driver/favourites). */
export default function DriverFavourites() {
  const t = useT();
  const p = t.driverPages.favourites;
  const { refreshSummary } = useDriver();
  const [items, setItems] = useState<VehicleCard[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    getFavourites()
      .then(setItems)
      .catch((err) => setError(errorText(err)));
  }, []);

  const remove = async (id: string) => {
    try {
      await setFavouriteListing(id, false);
      setItems((list) => list?.filter((v) => v.id !== id) ?? list);
      refreshSummary();
    } catch (err) {
      toast.error(err);
    }
  };

  return (
    <>
      <PageTitle className="mb-1">{p.title}</PageTitle>
      <p className="m-0 mb-5 text-sm text-muted">{p.intro}</p>
      {error ? (
        <p className="m-0 rounded-card bg-white p-6 text-sm text-tone-red-fg">{error}</p>
      ) : !items ? (
        <div className="h-40 animate-pulse rounded-card bg-white" aria-busy="true" />
      ) : items.length === 0 ? (
        <div className="rounded-card bg-white px-6 py-12 text-center">
          <p className="m-0 mb-3 text-sm text-muted">{p.empty}</p>
          <Link to="/vehicles" className="text-sm text-brand-link">
            {t.driverPages.bookings.find}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((v) => (
            <div key={v.id} className="grid gap-1.5">
              <VehicleCardItem vehicle={v} />
              <button
                type="button"
                onClick={() => void remove(v.id)}
                className="h-8 cursor-pointer rounded border-0 bg-transparent text-xs font-medium text-tone-red-fg hover:bg-tone-red-bg"
              >
                {p.remove}
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
